import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';

interface RequireAuthGateProps {
  children: React.ReactNode;
}

/**
 * Gate component that requires authentication
 * If user is not authenticated, opens the auth modal
 * If modal is closed and user is still not authenticated, redirects to home
 */
export default function RequireAuthGate({ children }: RequireAuthGateProps) {
  const { isAuthenticated, loading: isAuthLoading } = useAuth();
  const { isOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const location = useLocation();
  const navigate = useNavigate();

  // Open modal if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated && !isOpen) {
      openAuthModal({
        afterAuthRedirect: location.pathname,
        reason: 'protected-route',
        fromPath: location.pathname, // Track where modal was opened from
      });
    }
  }, [isAuthenticated, isAuthLoading, isOpen, location.pathname, openAuthModal]);

  // Listen for modal close events when not authenticated
  // Note: This effect handles the case where modal is closed without authentication
  // The modal close handler in LoginFlowModal will handle navigation after auth
  useEffect(() => {
    // Only redirect if modal closes and user is still not authenticated
    // Use a small delay to avoid race conditions
    if (!isAuthenticated && !isOpen && !isAuthLoading) {
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isOpen, isAuthLoading, navigate]);

  // Show loading state
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // If not authenticated, show a minimal placeholder UI behind the modal
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Sign In Required</h1>
        <p className="text-white/70 mb-6">
          Please sign in to access this page.
        </p>
        <button
          onClick={() => {
            closeAuthModal();
            navigate('/', { replace: true });
          }}
          className="text-white/60 hover:text-white transition-colors text-sm underline"
        >
          Go to Home
        </button>
      </div>
    </div>
  );
}

